import { THEMES, getTheme } from "@/utils/themes";
import { Link } from "react-router-dom";
import CustomizationPanel from "@/components/CustomizationPanel";

/**
 * Settings panel for the host inside a room.
 * - Pick a theme (applies to all players live) — Pro only
 * - Upload custom logo, background, text color — Pro only
 * - Close the room
 */
const RoomSettings = ({
  isOpen,
  onClose,
  currentTheme,
  onChangeTheme,
  onCloseRoom,
  isPro = false,
  roomCode,
  hasCustomLogo = false,
  hasCustomBackground = false,
  customTextColor = null,
  logoVersion = 0,
  bgVersion = 0,
}) => {
  if (!isOpen) return null;
  const active = getTheme(currentTheme);

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="room-settings-overlay"
    >
      <div
        className="w-full sm:max-w-md bg-[#0a0a0a] border-t sm:border border-white/10 sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag indicator (mobile) */}
        <div className="sm:hidden flex justify-center pt-3">
          <div className="w-12 h-1.5 bg-white/20 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-white/10">
          <h2
            className="text-2xl text-white"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            ROOM SETTINGS
          </h2>
          <button
            onClick={onClose}
            className="text-[#A1A1AA] hover:text-white p-1 text-xl"
            data-testid="settings-close-button"
          >
            ✕
          </button>
        </div>

        {/* Theme picker */}
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-white/90 font-semibold text-sm uppercase tracking-wider">
              Theme
            </h3>
            {!isPro && (
              <span
                className="text-[10px] uppercase tracking-wider bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/40 px-2 py-0.5 rounded-full"
                data-testid="theme-pro-badge"
              >
                Pro only
              </span>
            )}
          </div>
          <p className="text-[#A1A1AA] text-xs mb-4">
            {isPro
              ? "Changes apply for everyone in the room"
              : "Upgrade to Pro to unlock all 6 themes"}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {Object.values(THEMES).map((theme) => {
              const selected = theme.id === active.id;
              const locked = !isPro && theme.id !== "default";
              return (
                <button
                  key={theme.id}
                  onClick={() => !locked && onChangeTheme(theme.id)}
                  disabled={locked}
                  className={`relative overflow-hidden rounded-xl border-2 transition-all ${
                    locked ? "opacity-50 cursor-not-allowed" : "active:scale-95"
                  } ${
                    selected
                      ? "border-white shadow-[0_0_15px_rgba(255,255,255,0.4)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  style={{
                    height: 90,
                    background: theme.bgImage
                      ? `url(${theme.bgImage}) center/cover`
                      : theme.bgGradient,
                  }}
                  data-testid={`theme-option-${theme.id}`}
                >
                  <div className="absolute bottom-2 left-2 flex gap-1.5">
                    <span
                      className="w-3 h-3 rounded-full ring-1 ring-white/30"
                      style={{ background: theme.buzzerColor }}
                    />
                    <span
                      className="w-3 h-3 rounded-full ring-1 ring-white/30"
                      style={{ background: theme.accentColor }}
                    />
                    <span
                      className="w-3 h-3 rounded-full ring-1 ring-white/30"
                      style={{ background: theme.titleColor }}
                    />
                  </div>

                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
                    <div className="text-2xl mb-0.5">{theme.emoji}</div>
                    <div className="text-xs font-bold tracking-wide drop-shadow-md">
                      {theme.label.toUpperCase()}
                    </div>
                  </div>

                  {selected && (
                    <div className="absolute top-1.5 right-1.5 bg-white text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                      ✓
                    </div>
                  )}
                  {locked && (
                    <div className="absolute top-1.5 right-1.5 bg-[#FFEA00] text-black rounded-full px-2 py-0.5 flex items-center justify-center text-[10px] font-bold">
                      🔒
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {!isPro && (
            <Link
              to="/upgrade"
              onClick={onClose}
              className="block mt-4 w-full text-center px-4 py-2 bg-[#FFEA00] text-black text-sm font-bold rounded-xl hover:scale-[1.02] active:scale-95 transition-transform"
              data-testid="settings-upgrade-link"
            >
              ⭐ Upgrade to unlock all themes
            </Link>
          )}
        </div>

        {/* Pro customization: logo, background, text color */}
        <CustomizationPanel
          isPro={isPro}
          roomCode={roomCode}
          hasCustomLogo={hasCustomLogo}
          hasCustomBackground={hasCustomBackground}
          customTextColor={customTextColor}
          logoVersion={logoVersion}
          bgVersion={bgVersion}
          onClose={onClose}
        />

        {/* Danger zone */}
        <div className="px-5 pb-6 pt-2 border-t border-white/5">
          <h3 className="text-[#FF0055]/80 font-semibold mb-1 text-sm uppercase tracking-wider">
            Danger zone
          </h3>
          <p className="text-[#A1A1AA] text-xs mb-3">
            All players will be disconnected immediately.
          </p>
          <button
            onClick={onCloseRoom}
            className="w-full px-4 py-3 bg-[#FF0055]/20 text-[#FF0055] border border-[#FF0055]/40 font-semibold rounded-xl hover:bg-[#FF0055]/30 transition-all active:scale-95"
            data-testid="close-room-button"
          >
            🚪 Close Room
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomSettings;
